<template>
  <div class="editor-wrap" :class="currentView">
    <editor
      ref="editor"
      v-model="innerValue"
      @input="handleInput"
      @init="editorInit"
      :theme="$vuetify.theme.dark ? 'dracula' : 'xcode'"
      :options="options"
      lang="json"
      :width="width"
      :height="height"
    />
    <div class="editor-wrap__expand">
      <v-btn
        v-if="!isExpanded"
        icon
        class="editor-wrap__expand-full"
        @click="expandInterMediate"
        :title="isMedium ? 'Minimize' : 'Open compact'"
      >
        <v-icon v-if="!isMedium">mdi-arrow-expand</v-icon>
        <v-icon v-else>mdi-arrow-collapse</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  reactive,
  ref,
  toRefs,
  watch
} from '@vue/composition-api';

export default defineComponent({
  props: {
    code: {
      type: String,
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    editor: require('vue2-ace-editor')
  },
  name: 'CodeEditor',
  setup(props, { emit }) {
    const modalComposition = inject('modalComposition');
    const innerValue = ref(null);
    let editorInstance = null;

    const state = reactive({
      width: '100%',
      height: 100,
      isExpanded: false,
      isMedium: false,
      options: {},
      styleObject: {
        position: 'relative',
        width: '100%',
        height: '100%',
        margin: 'auto',
        right: 0,
        bottom: 0,
        top: 0,
        left: 0,
        zIndex: 1
      }
    });

    const editorInit = editor => {
      editorInstance = editor;
      require('brace/mode/json');
      require('brace/mode/javascript');
      require('brace/theme/xcode');
      require('brace/theme/dracula');
      require('brace/ext/searchbox');
    };

    const expandCode = () => {
      if (state.isExpanded) {
        state.height = 100;
        state.isExpanded = false;
      } else {
        state.height = '100%';
        state.isExpanded = true;
      }
    };

    const handleInput = () => {
      let value = editorInstance.getValue().trim();
      emit('input', value || null);
    };

    const expandInterMediate = () => {
      if (state.isMedium) {
        state.isMedium = false;
        state.height = 100;
        state.width = '100%';
      } else {
        state.isMedium = true;
        state.height = '100%';
        state.width = '100%';
      }
    };

    const currentView = computed(() => {
      if (state.isMedium) {
        return 'editor-intermediate';
      }
      if (state.isExpanded) {
        return 'editor-full';
      }
      return 'editor-collapsed';
    });

    onMounted(() => {
      editorInstance.getSession().on('changeAnnotation', function(e) {
        const isHasError = editorInstance
          .getSession()
          .getAnnotations()
          .some(item => item.type === 'error');

        emit('is-error', isHasError);
      });
      state.height = 100;
      state.styleObject.position = 'relative';
      state.styleObject.zIndex = 1;
      state.isExpanded = false;

      if (props.isDisabled) {
        editorInstance.setReadOnly(true);
      }
    });

    watch(
      () => props.code,
      () => {
        if (props.code) {
          innerValue.value = JSON.stringify(JSON.parse(props.code), null, 2);
        }
      },
      {
        immediate: true
      }
    );

    return {
      ...toRefs(state),
      editorInit,
      innerValue,
      expandCode,
      handleInput,
      expandInterMediate,
      currentView
    };
  }
});
</script>

<style lang="scss" scoped>
.editor-wrap {
  position: relative;
  overflow: hidden;

  .ace_editor {
    font-size: 14px;
  }

  &__expand {
    z-index: 10;
    position: absolute;
    top: 5px;
    right: 5px;
    .theme--dark.v-application {
      background-color: #1e1e1e !important;
    }

    &-medium {
      margin-left: 8px;
    }
  }
}

.editor-intermediate {
  position: fixed;
  border-radius: 4px;
  width: 80dvw;
  height: 88dvh;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  z-index: 12;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);

  @media (max-width: 824px) {
    width: 100dvw;
    height: 100dvh;
    border-radius: 0;
  }
}

.editor-full {
  position: fixed;
  width: 100dvw;
  height: 100dvh;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  z-index: 9;
}

.editor-collapsed {
  position: relative;
  height: auto;
}
</style>
